/**
 * The map bar's view group — [Game | Regions | Players | ★] — behaves as one
 * segmented control: exactly one segment is active at a time.
 *
 * - Game / Regions pick the map mode and close whatever is open.
 * - Players opens the players filter bar under the map bar. It is a tool for
 *   the live game view (play carries on while it is open), so it always sits
 *   over Game: opening it from Regions switches to Game.
 * - ★ opens the map rules overlay over the current mode.
 * - Players and ★ swap for one another; tapping the open one again closes it,
 *   which returns to the mode underneath.
 *
 * The players filter's selection lives here too, so closing the bar (by any
 * route) turns every filter off: a selection survives only while the Players
 * segment stays open.
 */
export type MapViewMode = 'game' | 'regions'
export type MapOverlayKind = 'players' | 'map'
export type ViewGroupTap = MapViewMode | MapOverlayKind

export interface ViewGroupState {
  mode: MapViewMode
  overlay: MapOverlayKind | null
  /** The players filter's selected chip keys (lib/playerFilter). */
  filter: readonly string[]
}

const NO_FILTER: readonly string[] = []

export function tapViewGroup(state: ViewGroupState, tap: ViewGroupTap): ViewGroupState {
  if (tap === 'game' || tap === 'regions') return { mode: tap, overlay: null, filter: NO_FILTER }
  if (state.overlay === tap) return { mode: state.mode, overlay: null, filter: NO_FILTER }
  if (tap === 'players') return { mode: 'game', overlay: 'players', filter: NO_FILTER }
  return { mode: state.mode, overlay: tap, filter: NO_FILTER }
}

/** Toggle one chip of the open players filter (a no-op while the bar is closed). */
export function toggleViewGroupFilter(state: ViewGroupState, key: string): ViewGroupState {
  if (state.overlay !== 'players') return state
  const filter = state.filter.includes(key) ? state.filter.filter((k) => k !== key) : [...state.filter, key]
  return { ...state, filter }
}

/** The one active segment: the open overlay, else the mode. */
export function activeViewSegment(state: ViewGroupState): ViewGroupTap {
  return state.overlay ?? state.mode
}
